import type {
    IDataTableBackgroundExport,
    IDataTableSource,
    IServerLoadOptions,
    IServerLoadResult,
    ServerRowPageLocator,
} from '@srm/ui/data-table/server';
import { isWholeSelectionRequest, withFacetLimit } from '@srm/ui/data-table/server';
import { EMPTY_FACET_LABEL } from '@srm/ui/data-table/filters';
import type { IDataTableExportColumn, IDataTableFacetValue } from '@srm/ui/data-table/types';
import { IDataGridService } from '@services/IDataGridService';
import { IDataGridColumnDescription } from '@models/response/IGridDescriptionResponse';
import { IGridGroupItem } from '@models/response/IGridResponse';
import { IGridExportSettings } from '@models/response/IGridExportSettings';
import { analyticsManager } from '@/analytics';
import { startGridExportJob } from '@services/backgroundJobs/startGridExportJob';
import { dataGridColumnId, DataGridRow } from './dataGridSchema';

/** Сколько значений тянуть в воронку шапки (как в DX — список, а не весь справочник). */
const HEADER_FILTER_LIMIT = 500;

export interface IDataGridSourceParams {
    service: IDataGridService;
    /** Ключ набора данных (папка поиска, таблица связей, журнал). */
    dataKey: string;
    /** Поля запроса (см. buildFieldsParam) на момент запроса: видимость колонок меняет их, не меняя выборки. */
    fields: () => string;
    /** Колонки схемы на момент запроса: позиция поля для воронки и серверные селекторы сортировки (`sortField`). */
    columns: () => IDataGridColumnDescription[];
    /** Начинается загрузка всей выборки (не раскрытой группы). Возвращённый обработчик получит её результат, если
     *  запрос не отменят и он не упадёт. Обработчик берётся до запроса: сверка выделения после действия видит только
     *  ответы на запросы, ушедшие после него. */
    onRootLoad?: (options: IServerLoadOptions) => ((result: IServerLoadResult<DataGridRow>) => void) | undefined;
}

/**
 * Источник списка DataGrid, курсор строки (`serverLocateRowPage`) и старт выгрузки фоновой задачей, если сервис их
 * умеет. Фоновую выгрузку в источник таблицы включает схема списка (`options.backgroundExport`).
 */
export type DataGridSource = Required<
    Pick<IDataTableSource<DataGridRow>, 'load' | 'keys' | 'facets' | 'facetPage' | 'export'>
> & {
    locateRowPage?: ServerRowPageLocator;
    exportInBackground?: IDataTableBackgroundExport['start'];
};

/** Колонки выгрузки в формате сервера: порядок и состав — из visibleIndex и visible. */
const exportSettings = (shown: IDataTableExportColumn[]): IGridExportSettings[] =>
    shown.map((column) => ({
        dataField: column.field,
        dataType: column.dataType != null ? String(column.dataType) : undefined,
        name: column.header,
        visible: true,
        visibleIndex: column.visibleIndex,
        width: column.width,
    }));

/** Сортировка таблицы держит id колонки (field), а серверу селектором уходит sortField, если схема задала его
 *  отдельно. */
function withSortSelectors(options: IServerLoadOptions, columns: IDataGridColumnDescription[]): IServerLoadOptions {
    if (!options.sort?.length) return options;
    const selectors = new Map(
        columns.filter((c) => c.sortField && c.sortField !== c.field).map((c) => [c.field, c.sortField] as const),
    );
    return { ...options, sort: options.sort.map((s) => ({ ...s, selector: selectors.get(s.selector) ?? s.selector })) };
}

/** Отмеченные значения, которых нет в выборке (их отрезали фильтры других колонок), остаются в воронке со
 *  счётчиком 0 — иначе их не снять точечно. */
export function withSelectedFacetValues(
    values: IDataTableFacetValue[],
    selected: readonly (string | null)[],
): IDataTableFacetValue[] {
    const known = new Set(values.map((value) => value.key));
    const missing = selected.filter((key) => !known.has(key));
    if (!missing.length) return values;
    return [...values, ...missing.map((key) => ({ key, label: key ?? EMPTY_FACET_LABEL, count: 0 }))];
}

/**
 * Источник серверного списка DataGrid над `IDataGridService`: выборка, ключи «выбрать все», значения воронки и
 * выгрузка в Excel идут одним транспортом (`fetchData` с полями и dxlo-параметрами). Источник один на набор
 * (`service` + `dataKey`); поля и колонки читаются на каждый запрос, поэтому их смена выборку другой не делает.
 */
export function createDataGridSource({
    service,
    dataKey,
    fields,
    columns,
    onRootLoad,
}: IDataGridSourceParams): DataGridSource {
    const load = async (
        loadOptions: IServerLoadOptions,
        signal: AbortSignal,
    ): Promise<IServerLoadResult<DataGridRow>> => {
        const requested = fields();
        if (!requested) return { rows: [], totalCount: 0 };
        const onLoaded = isWholeSelectionRequest(loadOptions) ? onRootLoad?.(loadOptions) : undefined;
        const options = withSortSelectors(loadOptions, columns());
        const res = await service.fetchData(dataKey, { options, fields: requested }, signal);
        let result: IServerLoadResult<DataGridRow>;
        // запрос с group — серверная группировка: элементы data — группы {key, caption, count}, а не строки
        if (loadOptions.group?.length) {
            const groups = (res.data.data ?? []) as unknown as IGridGroupItem[];
            result = {
                rows: [],
                totalCount: res.data.totalCount ?? 0,
                groups: groups.map((g) => ({ key: g.key, caption: g.caption ?? undefined, count: g.count ?? 0 })),
                groupCount: res.data.groupCount ?? groups.length,
            };
        } else {
            result = { rows: res.data.data ?? [], totalCount: res.data.totalCount ?? 0 };
        }
        if (!signal.aborted) onLoaded?.(result);
        return result;
    };

    // Ключи всей выборки для «выбрать все на всех страницах»: тот же запрос одной страницей и с одним полем.
    const keys = async (loadOptions: IServerLoadOptions, signal: AbortSignal) => {
        const requested = fields();
        if (!requested) return [];
        const options = { ...withSortSelectors(loadOptions, columns()), requireTotalCount: false };
        const res = await service.fetchData(dataKey, { options, fields: requested.split(',')[0] }, signal);
        return (res.data.data ?? []).map((row) => row.key);
    };

    // Значения воронки — как у DX: сервер группирует выборку по полю колонки и отдаёт ключи со счётчиками, skip/take
    // действуют по группам. Выборку (и поиск по значениям) собрала таблица, здесь берётся только её фильтр.
    const facetGroups = async (
        field: string,
        filter: unknown,
        skip: number,
        take: number,
        signal: AbortSignal,
    ): Promise<IDataTableFacetValue[] | undefined> => {
        const schemeColumns = columns();
        const column = schemeColumns.find((c, index) => dataGridColumnId(c, index) === field);
        if (!column?.field) return undefined;
        const position = schemeColumns.indexOf(column) + 1;
        const res = await service.fetchData(
            dataKey,
            {
                options: {
                    skip,
                    take,
                    requireTotalCount: false,
                    // общее число значений воронке не нужно, а сервер считал бы его по всей выборке
                    requireGroupCount: false,
                    ...(filter !== undefined ? { filter } : {}),
                    // выбранные значения уходят фильтром [field,'=',v] — группировка по тому же полю
                    group: [{ selector: column.field, desc: false, isExpanded: false }],
                },
                fields: String(position),
            },
            signal,
        );
        // ключ группы — значение поля: строка, число, дата строкой или флаг; подпись адресной книги, потока и
        // флага сервер кладёт в caption
        const groups = (res.data.data ?? []) as unknown as IGridGroupItem[];
        return groups.map((group) => {
            const empty = group.key == null || group.key === '';
            return {
                key: empty ? null : String(group.key),
                label: empty ? EMPTY_FACET_LABEL : (group.caption ?? String(group.key)),
                count: group.count ?? 0,
            };
        });
    };

    const facets: DataGridSource['facets'] = async ({ field, options, selected }, signal) => {
        const values = await facetGroups(field, options.filter, 0, HEADER_FILTER_LIMIT, signal);
        if (!values) return [];
        // сервер отдал столько групп, сколько просили, — дальше могут быть ещё: воронка пометит список
        const limit =
            values.length >= HEADER_FILTER_LIMIT ? { limit: HEADER_FILTER_LIMIT, by: 'values' as const } : undefined;
        return withFacetLimit(withSelectedFacetValues(values, selected), limit);
    };

    // Порция на одну группу больше: лишняя говорит, что список не кончился, и в ответ не идёт.
    const facetPage: DataGridSource['facetPage'] = async ({ field, options, skip, take }, signal) => {
        const values = await facetGroups(field, options.filter, skip, take + 1, signal);
        if (!values) return { values: [], hasMore: false };
        return { values: values.slice(0, take), hasMore: values.length > take };
    };

    // Файл собирает бэкенд по показанной выборке: страницы он обходит сам (skip/take затирает), порядок и состав
    // колонок берёт из visibleIndex и visible. Группировка в выборку не уходит — она клиентская.
    // метрика та же, что слал DX-грид: считаем выгрузки по спискам
    const trackExport = () =>
        analyticsManager.trackEvent({
            category: 'DataTable',
            action: 'Клик по кнопке экспорта таблицы данных',
            name: `data-grid-export--${dataKey}`,
        });

    const exportToExcel: DataGridSource['export'] = async ({ loadOptions, columns: shown, maxItemsCount }) => {
        try {
            await service.exportData(
                dataKey,
                loadOptions ?? { skip: 0, take: 0 },
                exportSettings(shown),
                maxItemsCount,
            );
        } catch {
            // об отказе уже сказал загрузчик файла (FilesService) — второе уведомление было бы шумом
            return;
        }
        trackExport();
    };

    const exportDataInBackground = service.exportDataInBackground?.bind(service);
    const exportInBackground: DataGridSource['exportInBackground'] = exportDataInBackground
        ? async ({ loadOptions, columns: shown, maxItemsCount, suggested, origin }) => {
              const outcome = await startGridExportJob(
                  () =>
                      exportDataInBackground(
                          dataKey,
                          loadOptions ?? { skip: 0, take: 0 },
                          exportSettings(shown),
                          maxItemsCount,
                      ),
                  { suggested, from: origin },
              );
              if (outcome === 'started') trackExport();
              return outcome;
          }
        : undefined;

    const fetchRowPage = service.fetchRowPage?.bind(service);
    const locateRowPage: ServerRowPageLocator | undefined = fetchRowPage
        ? async (rowId, loadOptions, signal) => {
              const options = withSortSelectors(loadOptions, columns());
              const response = await fetchRowPage(dataKey, rowId, { options, fields: '' }, signal);
              // пустая выборка без строки отдаёт 204 → axios видит пустую строку
              const rawPage: unknown = response.data;
              if (rawPage == null || rawPage === '') return undefined;
              const page = Number(rawPage);
              return Number.isInteger(page) && page >= 0 ? page : undefined;
          }
        : undefined;

    return { load, keys, facets, facetPage, export: exportToExcel, locateRowPage, exportInBackground };
}
